// ---------------------------------------------------------------------------
// Klaviyo onsite (klaviyo.js) client helpers — MARKETING analytics only.
//
// The klaviyo.js loader (see KlaviyoScript.tsx) installs a global push-queue at
// `window.klaviyo`. All calls here are guarded: they no-op safely on the server
// and whenever the script is absent (e.g. NEXT_PUBLIC_KLAVIYO_PUBLIC_KEY unset),
// so callers never need their own guards.
//
// Identity/consent/identifier writes of record are server-side (KlaviyoProfileService).
// Client `identify` here is ONLY the anon-cookie stitch (§4.4), and per feasibility
// amendment #25 accepts unprefixed `email`/`phone_number`/name keys only — never
// `$id`/`external_id`.
// ---------------------------------------------------------------------------

type KlaviyoQueue = { push: (args: unknown[]) => void };

function getKlaviyo(): KlaviyoQueue | undefined {
  if (typeof window === "undefined") return undefined;
  const klaviyo = (window as unknown as { klaviyo?: KlaviyoQueue }).klaviyo;
  if (!klaviyo || typeof klaviyo.push !== "function") return undefined;
  return klaviyo;
}

export type KlaviyoIdentifyTraits = {
  email?: string | null;
  phone_number?: string | null;
  first_name?: string | null;
  last_name?: string | null;
};

/**
 * Stitch the anonymous onsite-tracking cookie to a known profile after auth.
 * Unprefixed keys only — no `$id`/`external_id` (silently ignored by klaviyo.js).
 */
export function klaviyoIdentify(traits: KlaviyoIdentifyTraits): void {
  const klaviyo = getKlaviyo();
  if (!klaviyo) return;
  const payload: Record<string, string> = {};
  for (const [key, value] of Object.entries(traits)) {
    if (value) payload[key] = value;
  }
  if (Object.keys(payload).length === 0) return;
  try {
    klaviyo.push(["identify", payload]);
  } catch {
    // never let analytics break the app
  }
}

/** Emit a client-side marketing event (e.g. "Viewed Product", "Added to Cart"). */
export function klaviyoTrack(event: string, properties: Record<string, unknown>): void {
  const klaviyo = getKlaviyo();
  if (!klaviyo) return;
  try {
    klaviyo.push(["track", event, properties]);
  } catch {
    // never let analytics break the app
  }
}
